// LabelSelectionContext.js - New context for Labels
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";

const LabelSelectionContext = createContext();

export const LabelSelectionProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  // Filters state
  const [filters, setFilters] = useState({
    name: "",
    description: "",
    priorityLevel: "",
    courtRef: "",
    fromDate: "",
    toDate: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 5,
    hasMore: true,
    totalLabels: 0,
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Store filtered labels
  const [filteredLabels, setFilteredLabels] = useState([]);

  const lastUpdateTimeRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerLabelRefresh = useCallback(() => {
    setRefreshKey((prevKey) => prevKey + 1);
  }, []);

  const setFilteredLabelsCallback = useCallback((labels) => {
    const now = Date.now();

    // Throttling to prevent excessive updates
    if (now - lastUpdateTimeRef.current < 50) {
      return;
    }
    lastUpdateTimeRef.current = now;

    // Always update with new data
    setFilteredLabels(Array.isArray(labels) ? [...labels] : []);
  }, []);

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    // Pagination
    // params.set("page", pagination.page.toString());
    // params.set("limit", pagination.limit.toString());

    // Search term
    if (searchTerm && searchTerm.trim()) {
      params.set("search", searchTerm.trim());
    }

    // FIXED: Apply all filters with proper empty string checking
    if (filters.name && filters.name.trim() !== "") {
      params.set("name", filters.name.trim());
    }
    if (filters.description && filters.description.trim() !== "") {
      params.set("description", filters.description.trim());
    }
    if (filters.priorityLevel && filters.priorityLevel.trim() !== "") {
      params.set("priorityLevel", filters.priorityLevel.trim());
    }
    if (filters.courtRef && filters.courtRef.trim() !== "") {
      params.set("courtRef", filters.courtRef.trim());
    }
    if (filters.fromDate && filters.fromDate.trim() !== "") {
      params.set("fromDate", filters.fromDate.trim());
    }
    if (filters.toDate && filters.toDate.trim() !== "") {
      params.set("toDate", filters.toDate.trim());
    }

    return params.toString();
  }, [pagination, searchTerm, filters]);

  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      page: 0,
      hasMore: true,
    }));
  }, []);

  // Clear all filters and reset state
  const clearAllFilters = useCallback(() => {

    setFilters({
      name: "",
      description: "",
      priorityLevel: "",
      courtRef: "",
      fromDate: "",
      toDate: "",
    });

    setSearchTerm("");
    resetPagination();

    // Clear the filtered labels
    setFilteredLabels([]);
  }, [resetPagination]);

  // Stable setters
  const setFiltersStable = useCallback(
    (newFilters) => {
      setFilters((prevFilters) => {
        const hasChanged = Object.keys(newFilters).some(
          (key) => newFilters[key] !== prevFilters[key]
        );

        if (hasChanged) {
          resetPagination();
          return newFilters;
        }

        return prevFilters;
      });
    },
    [resetPagination]
  );

  const setSearchTermStable = useCallback(
    (term) => {
      if (term !== searchTerm) {
        setSearchTerm(term);
        resetPagination();
      }
    },
    [searchTerm, resetPagination]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== "" ||
      Object.values(filters).some((value) => value && value.trim() !== "")
    );
  }, [searchTerm, filters]);

  // Update pagination info
  // const updatePaginationInfo = useCallback((totalLabels, hasMore) => {
  //   setPagination((prev) => ({
  //     ...prev,
  //     totalLabels,
  //     hasMore,
  //   }));
  // }, []);
  const updatePaginationInfo = useCallback((totalLabels, hasMore) => {
  setPagination(prev => ({
    ...prev,
    totalLabels,
    hasMore
  }));
}, []);

  // Load more labels (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore) {
      setPagination((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  }, [pagination.hasMore]);

  const contextValue = useMemo(
    () => ({
      // Filters
      filters,
      setFilters: setFiltersStable,

      // Search
      searchTerm,
      setSearchTerm: setSearchTermStable,

      // Pagination
      pagination,
      setPagination,
      resetPagination,
      updatePaginationInfo,
      loadMore,

      // Filtered labels
      filteredLabels,
      setFilteredLabels: setFilteredLabelsCallback,
      refreshKey,
      triggerLabelRefresh,

      // Utilities
      buildQueryParams,
      clearAllFilters,
      hasActiveFilters,
      setIsLoading,
      isLoading,
    }),
    [
      filters,
      setFiltersStable,
      searchTerm,
      setSearchTermStable,
      pagination,
      resetPagination,
      updatePaginationInfo,
      loadMore,
      filteredLabels,
      setFilteredLabelsCallback,
      buildQueryParams,
      clearAllFilters,
      hasActiveFilters,
      refreshKey,
      triggerLabelRefresh,
      setIsLoading,
      isLoading,
    ]
  );

  return (
    <LabelSelectionContext.Provider value={contextValue}>
      {children}
    </LabelSelectionContext.Provider>
  );
};

export const useLabelSelection = () => {
  const context = useContext(LabelSelectionContext);
  if (!context) {
    throw new Error(
      "useLabelSelection must be used within a LabelSelectionProvider"
    );
  }
  return context;
};
